import { useEffect, useState } from "react";
import Dialog from "./Dialog";
import Icon from "./Icon";
import { notifyError } from "./useBusiness";
import PromoBanner from "../menu/PromoBanner.jsx";
import { themeOf, themeStyle } from "../../menuTheme.js";
import { coverFileProblem } from "../../cover.js";
import { PRO_TRIAL_DAYS, PROMOS_PRO_ONLY } from "../../plans.js";
import { cleanPromo, MAX_PROMOS, PROMO_DAYS, PROMO_SECONDS, PROMO_TEXT_MAX, PROMO_TITLE_MAX, promoProblem, promosAvailable, promosOf, promoState, promoWhen } from "../../promos.js";
import "../../menu.css";

const newId = () => Date.now().toString(36) + Math.random().toString(36).slice(2, 6);

// Alta y edición de una promo, con la vista previa de cómo queda en el menú.
const PromoForm = ({ business, products, promo, onSave, onCancel }) => {

    const [form, setForm] = useState(() => cleanPromo(promo || { id: newId() }));
    const [file, setFile] = useState(null);
    const [preview, setPreview] = useState("");
    const [dropImage, setDropImage] = useState(false);
    const [saving, setSaving] = useState(false);

    useEffect(() => () => preview && URL.revokeObjectURL(preview), [preview]);

    const set = (key, value) => setForm((f) => ({ ...f, [key]: value }));
    const toggleDay = (day) => set("dias", form.dias.includes(day) ? form.dias.filter((d) => d !== day) : [...form.dias, day]);

    const pick = (e) => {
        const chosen = e.target.files[0];
        e.target.value = "";
        if (!chosen) return;
        const problem = coverFileProblem(chosen);
        if (problem) {
            notifyError(problem);
            return;
        }
        setFile(chosen);
        setPreview(URL.createObjectURL(chosen));
        setDropImage(false);
    }

    const removeImage = () => {
        setFile(null);
        setPreview("");
        setDropImage(true);
    }

    const image = preview || (dropImage ? "" : form.imagen);
    // Vista previa: el preview local de la foto no es un link de Storage, por eso va aparte.
    const shown = { ...cleanPromo(form), imagen: image, titulo: form.titulo.trim() || "Título de la promo" };

    const submit = async (e) => {
        e.preventDefault();
        const clean = cleanPromo(form);
        const problem = promoProblem(clean);
        if (problem) {
            notifyError(problem);
            return;
        }
        setSaving(true);
        const ok = await onSave(clean, file, dropImage);
        if (!ok) setSaving(false);
    }

    const sorted = [...products].sort((a, b) => (a.name || "").localeCompare(b.name || "", "es"));

  return (
    <form className="lp-panel-form" onSubmit={submit} noValidate>
        <div>
            <span className="lp-panel-label">Así se ve en tu menú</span>
            <div className="lp-menu lp-menu-preview lp-panel-promo-preview" style={themeStyle(themeOf(business))} aria-hidden="true">
                <PromoBanner promos={[shown]} products={products} onOpen={() => {}} preview></PromoBanner>
            </div>
        </div>

        <div className="lp-field">
            <label htmlFor="pr-title">Título</label>
            <input id="pr-title" type="text" required maxLength={PROMO_TITLE_MAX} placeholder="Ej.: 2x1 en pizzas" value={form.titulo} onChange={(e) => set("titulo", e.target.value)}/>
        </div>

        <div className="lp-field">
            <label htmlFor="pr-text">Detalle (opcional)</label>
            <input id="pr-text" type="text" maxLength={PROMO_TEXT_MAX} placeholder="Ej.: Todos los martes, de 20 a 23" value={form.texto} onChange={(e) => set("texto", e.target.value)}/>
        </div>

        <div className="lp-field">
            <span className="lp-panel-label">Foto (opcional)</span>
            <div className="lp-panel-actions">
                <label className="lp-btn lp-btn-secondary lp-panel-btn-sm" htmlFor="pr-image">{image ? "Cambiar foto" : "Elegir foto"}</label>
                <input id="pr-image" className="lp-panel-file" type="file" accept="image/*" onChange={pick}/>
                {image && <button type="button" className="lp-text-link" onClick={removeImage}>Sacar foto</button>}
            </div>
            <span className="lp-field-hint">Mejor horizontal. Sin foto, la promo va con el color de los botones de tu menú.</span>
        </div>

        <div className="lp-field">
            <label htmlFor="pr-product">Al tocarla, abrir un producto (opcional)</label>
            <select id="pr-product" value={form.producto} onChange={(e) => set("producto", e.target.value)}>
                <option value="">Ninguno</option>
                {sorted.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
        </div>

        <div className="lp-field">
            <span className="lp-panel-label" id="pr-days">Días</span>
            <div className="lp-panel-tag-picks" role="group" aria-labelledby="pr-days">
                {PROMO_DAYS.map((d) => (
                    <button key={d.value} type="button" aria-pressed={form.dias.includes(d.value)} aria-label={d.name} className={form.dias.includes(d.value) ? "lp-panel-chip lp-panel-chip-on" : "lp-panel-chip"} onClick={() => toggleDay(d.value)}>{d.short}</button>
                ))}
            </div>
            <span className="lp-field-hint">{form.dias.length && form.dias.length < 7 ? "Solo se ve esos días." : "Sin elegir, se ve todos los días."}</span>
        </div>

        <div className="lp-panel-grid-2">
            <div className="lp-field">
                <label htmlFor="pr-from-h">Desde la hora (opcional)</label>
                <input id="pr-from-h" type="time" value={form.horaDesde} onChange={(e) => set("horaDesde", e.target.value)}/>
            </div>
            <div className="lp-field">
                <label htmlFor="pr-to-h">Hasta la hora</label>
                <input id="pr-to-h" type="time" value={form.horaHasta} onChange={(e) => set("horaHasta", e.target.value)}/>
            </div>
        </div>

        <div className="lp-panel-grid-2">
            <div className="lp-field">
                <label htmlFor="pr-from">Empieza el (opcional)</label>
                <input id="pr-from" type="date" value={form.desde} onChange={(e) => set("desde", e.target.value)}/>
            </div>
            <div className="lp-field">
                <label htmlFor="pr-to">Termina el (opcional)</label>
                <input id="pr-to" type="date" min={form.desde || undefined} value={form.hasta} onChange={(e) => set("hasta", e.target.value)}/>
            </div>
        </div>

        <div className="lp-panel-actions">
            <button className="lp-btn lp-btn-primary" type="submit" disabled={saving}>{saving ? "Guardando..." : promo ? "Guardar cambios" : "Crear promo"}</button>
            <button className="lp-btn lp-btn-secondary" type="button" onClick={onCancel}>Cancelar</button>
        </div>
    </form>
  )
}

// Promociones del banner del menú (Pro): lista, orden, pausa y alta/edición.
const PromosCard = ({ data }) => {

    const { business, products } = data;
    const available = promosAvailable(business, PROMOS_PRO_ONLY);
    const promos = promosOf(business);
    const [editing, setEditing] = useState(null);
    const [deleting, setDeleting] = useState(null);
    const [busy, setBusy] = useState(false);

    if (!available) {
        return (
            <div className="lp-panel-stack">
                <p>Mostrá tus promos arriba de todo en el menú: el 2x1 de los martes, el happy hour o el plato del día. Con foto, y se pasan solas.</p>
                <p className="lp-panel-muted">Las promociones son del plan Pro.</p>
                <a className="lp-btn lp-btn-primary lp-panel-left" href="#cuenta">Probar Pro gratis {PRO_TRIAL_DAYS} días</a>
            </div>
        )
    }

    const move = (from, to) => {
        const next = [...promos];
        const [item] = next.splice(from, 1);
        next.splice(to, 0, item);
        data.reorderPromos(next);
    }
    const toggle = (promo) => data.reorderPromos(promos.map((p) => p.id === promo.id ? { ...p, activa: !p.activa } : p));

    const confirmDelete = async () => {
        setBusy(true);
        if (await data.deletePromo(deleting)) setDeleting(null);
        setBusy(false);
    }

  return (
    <div className="lp-panel-stack">
        <p className="lp-panel-muted">Se ven arriba de todo en tu menú y pasan solas cada {PROMO_SECONDS} segundos. Podés elegir qué días, en qué horario y hasta cuándo se muestran. Hasta {MAX_PROMOS} promos.</p>

        {promos.length > 0 &&
            <ul className="lp-panel-promos">
                {promos.map((p, i) => {
                    const state = promoState(p);
                    return (
                        <li key={p.id} className={p.activa ? "lp-panel-promo" : "lp-panel-promo lp-panel-promo-off"}>
                            {p.imagen ? <img className="lp-panel-promo-thumb" src={p.imagen} alt=""/> : <span className="lp-panel-promo-thumb" style={{ background: themeOf(business).buttonColor }} aria-hidden="true"></span>}
                            <span className="lp-panel-promo-info">
                                <strong>{p.titulo}</strong>
                                <span className="lp-panel-muted">{promoWhen(p)}</span>
                                <span className={state.live ? "lp-panel-promo-state lp-panel-promo-live" : "lp-panel-promo-state"}>{state.label}</span>
                            </span>
                            <span className="lp-panel-promo-actions">
                                <button type="button" className="lp-panel-switch lp-panel-switch-plain" role="switch" aria-checked={p.activa} aria-label={`Mostrar ${p.titulo}`} title={p.activa ? "Pausar" : "Activar"} onClick={() => toggle(p)}>
                                    <span className="lp-panel-switch-track"><span className="lp-panel-switch-knob"></span></span>
                                </button>
                                <button type="button" className="lp-panel-icon-btn lp-panel-promo-up" disabled={i === 0} aria-label={`Subir ${p.titulo}`} title="Subir" onClick={() => move(i, i - 1)}><Icon name="up" size={18}></Icon></button>
                                <button type="button" className="lp-panel-icon-btn lp-panel-promo-down" disabled={i === promos.length - 1} aria-label={`Bajar ${p.titulo}`} title="Bajar" onClick={() => move(i, i + 1)}><Icon name="up" size={18}></Icon></button>
                                <button type="button" className="lp-panel-icon-btn" aria-label={`Editar ${p.titulo}`} title="Editar" onClick={() => setEditing(p)}><Icon name="edit" size={18}></Icon></button>
                                <button type="button" className="lp-panel-icon-btn" aria-label={`Borrar ${p.titulo}`} title="Borrar" onClick={() => setDeleting(p)}><Icon name="trash" size={18}></Icon></button>
                            </span>
                        </li>
                    )
                })}
            </ul>
        }

        <button type="button" className="lp-btn lp-btn-primary lp-panel-left" disabled={promos.length >= MAX_PROMOS} onClick={() => setEditing("new")}>
            <Icon name="plus"></Icon> Nueva promo
        </button>

        <Dialog open={!!editing} title={editing === "new" ? "Nueva promo" : "Editar promo"} onClose={() => setEditing(null)} wide>
            {editing &&
                <PromoForm key={editing === "new" ? "new" : editing.id} business={business} products={products} promo={editing === "new" ? null : editing} onCancel={() => setEditing(null)} onSave={async (promo, file, dropImage) => {
                    const ok = await data.savePromo(promo, file, dropImage);
                    if (ok) setEditing(null);
                    return ok;
                }}></PromoForm>
            }
        </Dialog>

        <Dialog open={!!deleting} title="Borrar promo" onClose={() => setDeleting(null)}>
            <p className="lp-panel-muted">¿Seguro que querés borrar <strong>{deleting?.titulo}</strong>? Deja de verse en tu menú.</p>
            <div className="lp-panel-actions">
                <button type="button" className="lp-btn lp-panel-btn-danger" disabled={busy} onClick={confirmDelete}>{busy ? "Borrando..." : "Borrar"}</button>
                <button type="button" className="lp-btn lp-btn-secondary" onClick={() => setDeleting(null)}>Cancelar</button>
            </div>
        </Dialog>
    </div>
  )
}

export default PromosCard
